/**
 * The checks every authoring operation asks before it changes anything, and
 * the batch preparation the per-entry operations share.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Keyframes } from '../../behaviors/keyframes';
import type { UndoCommand } from '../undo/UndoCommand';
import { getUndoManager } from '../undo/UndoManager';
import { copyForInsideEdit, needsReferenceCopy, readOnlyRefusal } from './referenceEdit';
import { rollBack } from './steps';
import { PLAY_MODE_REFUSAL, UNDO_IN_PROGRESS_REFUSAL, type AuthoringPorts, type BatchRefusal, type Refusal, type RefusalCode } from './types';

/** Why no edit may start now, or why one of `objects` can't be edited at all. */
export function editRefusal(scene: TraceScene, objects: readonly SceneObject[]): Refusal | null {
    if (scene.isPlayMode()) {
        return PLAY_MODE_REFUSAL;
    }
    if (getUndoManager().isUndoRedoing) {
        return UNDO_IN_PROGRESS_REFUSAL;
    }
    const disposed = objects.find(object => object.transform.isDisposed());
    return disposed ? disposedRefusal(disposed) : null;
}

export function disposedRefusal(object: SceneObject): Refusal {
    return refuse('DISPOSED', `"${object.name}" has been deleted`, object);
}

export function refuse(code: RefusalCode, message: string, object?: SceneObject): Refusal {
    return object ? { code, message, object } : { code, message };
}

/**
 * Why `edit` (a noun phrase, such as "a pose") may not be set on `object` now:
 * a keyframe is selected on it or an ancestor. The editor routes edits under a
 * selected keyframe into that keyframe, so writing Base would be out of sight.
 */
export function keyframeRefusal(object: SceneObject, edit: string): Refusal | null {
    for (let node: SceneObject | null = object; node; node = node.getParent()) {
        const keyframe = node.getBehavior(Keyframes)?.getActiveKeyframe();
        if (keyframe) {
            return refuse(
                'STATE_ROUTED',
                `The keyframe "${keyframe}" of "${node.name}" is selected, and ${edit} set now on "${object.name}" would change Base instead; return "${node.name}" to Base to edit it`,
                object,
            );
        }
    }
    return null;
}

/** The one scene every object belongs to (`scene`, when given). Objects from two scenes are a caller's bug. */
export function sceneOf(objects: readonly SceneObject[], scene: TraceScene = objects[0].traceScene): TraceScene {
    if (objects.some(object => object.traceScene !== scene)) {
        throw new Error('[authoring] the objects belong to different scenes');
    }
    return scene;
}

/** A batch, checked and ready to apply. */
export interface PreparedBatch<E> {
    /** The entries that may be applied, in the order given, each with its index in the batch. */
    ready: { entry: E; index: number }[];
    /** In the batch's order. */
    refused: BatchRefusal[];
    /** The copy-on-edit the ready entries needed, already applied; the batch's first step. */
    steps: UndoCommand[];
    /** Files the copy could not bring into the folder (see `copyForInsideEdit`). */
    orphaned: string[];
}

/**
 * Check each entry of a batch and copy the placed references the passing ones
 * land inside (`copyForInsideEdit`), so they can then be applied to nodes the
 * scene owns. An entry `changes` says leaves its object as it is copies
 * nothing. While the scene plays or an undo runs, every entry is refused;
 * otherwise each entry is refused on its own: a deleted object, one the host
 * holds read-only, `check`'s answer, or a copy that cannot be made, which refuses every entry under any
 * placement the copy would unpack, whether the entry changes anything or not. Once the copy has run, the scene and `late` are
 * asked again: if play started, an object an entry `needs` was deleted, or
 * `late` refuses an entry, the copy is taken back and every entry refused,
 * each with its own reason where it has one.
 */
export async function prepareBatch<E extends { object: SceneObject }>(
    entries: readonly E[],
    ports: AuthoringPorts,
    options: {
        check?: (entry: E) => Refusal | null;
        /** What of `check` may have changed while a copy ran, asked after it. Default: nothing. */
        late?: (entry: E) => Refusal | null;
        changes?: (entry: E) => boolean;
        /** Every object an entry needs, asked again after the copy. Default: its own. */
        needs?: (entry: E) => SceneObject[];
    } = {},
): Promise<PreparedBatch<E>> {
    const scene = sceneOf(entries.map(entry => entry.object));
    const refusals: (Refusal | undefined)[] = [];
    const done = (ready: number[], steps: UndoCommand[], orphaned: string[]): PreparedBatch<E> => ({
        ready: ready.map(index => ({ entry: entries[index], index })),
        refused: refusals.flatMap((refusal, index) =>
            refusal ? [{ ...refusal, object: refusal.object ?? entries[index].object, entry: index }] : []),
        steps,
        orphaned,
    });

    const everyEntry = editRefusal(scene, []);
    const passing: number[] = [];
    for (const [index, entry] of entries.entries()) {
        refusals[index] = everyEntry
            ?? (entry.object.transform.isDisposed() ? disposedRefusal(entry.object) : null)
            ?? readOnlyRefusal(entry.object, ports)
            ?? options.check?.(entry)
            ?? undefined;
        if (!refusals[index]) passing.push(index);
    }

    // Every entry under a placement a copy would unpack; the ones that change something need the copy.
    const within = passing.filter(index => needsReferenceCopy(entries[index].object));
    const inside = within.filter(index => options.changes?.(entries[index]) ?? true);
    if (inside.length === 0) return done(passing, [], []);

    const copy = await copyForInsideEdit(inside.map(index => entries[index].object), ports);
    let candidates = passing;
    let steps: UndoCommand[] = [];
    let orphaned: string[] = [];
    if ('refused' in copy) {
        // A refused copy takes back the copies it had made, so no entry under one is left to edit.
        for (const index of within) refusals[index] = { ...copy.refused, object: entries[index].object };
        candidates = passing.filter(index => !within.includes(index));
    } else {
        steps = copy.command ? [copy.command] : [];
        orphaned = copy.orphaned;
    }

    // Asked again, whatever the copy did: the scene, or an entry's own answer
    // (a state started showing), may have changed while it ran. Any refusal now
    // refuses the batch, so a copy is never taken back from under an entry.
    const needs = options.needs ?? ((entry: E) => [entry.object]);
    const sceneLate = editRefusal(scene, candidates.flatMap(index => needs(entries[index])));
    const own = new Map<number, Refusal>();
    if (!sceneLate) {
        for (const index of candidates) {
            const refusal = options.late?.(entries[index]);
            if (refusal) own.set(index, refusal);
        }
    }
    const cause = sceneLate ?? own.values().next().value;
    if (cause) {
        await rollBack(steps, cause);
        for (const index of candidates) {
            const refusal = own.get(index) ?? cause;
            refusals[index] = { ...refusal, object: refusal.object ?? entries[index].object };
        }
        return done([], [], []);
    }
    return done(candidates, steps, orphaned);
}
